/**
 * Dismissible error banner for dashboard sections.
 * Red-tinted with alert icon, error message, and optional retry button.
 */

import { AlertTriangle, X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/Button';

// ============================================================================
// TYPES
// ============================================================================

export interface ErrorBannerProps {
  message: string;
  onRetry?: () => void;
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ErrorBanner({ message, onRetry, className }: ErrorBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg bg-danger/10 border border-danger/20 ${className ?? ''}`}
      role="alert"
    >
      <AlertTriangle className="w-5 h-5 text-danger shrink-0" />
      <p className="text-sm text-danger flex-1">{message}</p>
      {onRetry && (
        <Button variant="ghost" size="sm" onClick={onRetry} className="text-danger">
          Retry
        </Button>
      )}
      <button
        onClick={() => setDismissed(true)}
        className="text-danger/60 hover:text-danger transition-colors"
        aria-label="Dismiss error"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
